import { Alert, Spin } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import OllieIcon from 'src/components/icons/OllieIcon'
import { sendOllieMessage } from 'src/features/OllieSlice'
import useOllie from 'src/hooks/useOllie'
import useOllieFindings from 'src/hooks/useOllieFindings'
import useOllieOpening from 'src/hooks/useOllieOpening'
import { useCanAccess } from 'src/hooks/useIsBlocked'
import {
  OLLIE_FINDING_ACTION_MODES,
  OLLIE_FINDING_CARD_COPY,
  buildOllieOpeningDisplay,
  filterOllieStarterPrompts,
  shouldShowOllieStarterPrompts,
  isOllieChipAskDisabled,
} from 'src/utils/ollieFindingCards'
import { OLLIE_FINDING_SURFACES } from 'src/utils/ollieFindingsApi'
import {
  fallbackOllieHiddenUntil,
  ollieHiddenUntilLabel,
  resolveOllieHiddenUntil,
} from 'src/utils/ollieSnoozeConfirm'
import {
  OLLIE_TAP_TYPES,
  listUsableOllieChips,
  withoutChipsMatchingReply,
  ollieFreeFollowUpLine,
  resolveOllieActionTap,
  resolveOllieChipTap,
} from 'src/utils/ollieActionFlows'
import { buildOllieMomentDisplay } from 'src/utils/ollieMoments'
import { beginOllieOrderingHandoff } from 'src/utils/ollieOrderingHandoff'
import styled from 'styled-components/macro'
import OllieActionSheet from './OllieActionSheet'
import OllieChatInput from './OllieChatInput'
import OllieFindingChips from './OllieFindingChips'
import OllieFindingCards from './OllieFindingCards'
import OllieMessageBubble from './OllieMessageBubble'
import OllieMomentCard from './OllieMomentCard'
import OllieReportFinding from './OllieReportFinding'
import {
  isOllieReplyRevealing,
  latestOllieReplyId,
  ollieReopenScrollTargetId,
} from 'src/utils/ollieReplyReveal'
import OllieStarterPrompts from './OllieStarterPrompts'
import OllieTypingIndicator from './OllieTypingIndicator'
import OllieRetryRow from './OllieRetryRow'

const OLLIE_SUBTITLE = 'Keeps an eye on your kitchen'

const OllieChatPanel = ({
  visible,
  onClose,
  onShowPaywall,
  pageContext: pageContextProp,
  draftMessage = '',
  onDraftConsumed,
  pendingFindingCheckId = null,
  pendingFindingAction = null,
  pendingFindingAsk = null,
  onPendingFindingConsumed,
}) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const pageContext = pageContextProp || location.pathname
  const canOrder = useCanAccess('shopping-lists')
  const messagesEndRef = useRef(null)
  const messageScrollRef = useRef(null)
  const [answerToShowId, setAnswerToShowId] = useState(null)
  const openedThreadRef = useRef(null)
  const hasInitializedRef = useRef(false)
  const wasSendingRef = useRef(false)
  const replyBeforeSendRef = useRef(null)
  const [revealHold, setRevealHold] = useState(null)
  const [animatingMessageId, setAnimatingMessageId] = useState(null)

  const {
    venueId,
    isAvailable,
    enabled,
    ready,
    introMessage,
    finding,
    chips,
    followUp,
    messages,
    starterPrompts,
    usage,
    usageWarning,
    limitReached,
    error,
    conversationId,
    isLoading,
    isLoadingMessages,
    isSending,
    isSubmittingFeedback,
    lastFailedSend,
    checkStatus,
    openConversation,
    openFindingConversation,
    loadMessages,
    loadStarterPrompts,
    sendMessage,
    sendChip,
    askFindingWhy,
    tapReplyButton,
    submitFeedback,
    retrySend,
    clearThread,
  } = useOllie()

  const {
    status: openingStatus,
    opening,
    load: loadOpening,
  } = useOllieOpening()
  const { markShown, markClicked, snooze, unsnooze } = useOllieFindings({
    autoLoad: false,
  })
  const isFindingThread = Boolean(finding?.checkId)
  const openingLoadedRef = useRef(false)
  const shownCheckIdsRef = useRef(new Set())
  const snoozeRequestsRef = useRef({})
  const [hiddenCheckIds, setHiddenCheckIds] = useState([])
  const [snoozedRows, setSnoozedRows] = useState({})
  const [activeAction, setActiveAction] = useState(null)
  const [actionNotice, setActionNotice] = useState(null)
  const [moment, setMoment] = useState(null)
  const [feedbackFollowUps, setFeedbackFollowUps] = useState({})
  const [openingFindingThread, setOpeningFindingThread] = useState(false)
  const goingToFinding = Boolean(pendingFindingCheckId || openingFindingThread)

  useEffect(() => {
    if (!visible) {
      openingLoadedRef.current = false
      shownCheckIdsRef.current = new Set()
      snoozeRequestsRef.current = {}
      setHiddenCheckIds([])
      setSnoozedRows({})
      setActiveAction(null)
      setActionNotice(null)
      setMoment(null)
      return
    }
    if (
      !venueId ||
      isFindingThread ||
      goingToFinding ||
      openingLoadedRef.current
    ) {
      return
    }
    openingLoadedRef.current = true
    loadOpening(pageContext)
  }, [
    visible,
    venueId,
    isFindingThread,
    goingToFinding,
    pageContext,
    loadOpening,
  ])

  const openingDisplay = useMemo(
    () =>
      buildOllieOpeningDisplay({
        opening,
        status: openingStatus,
        introMessage,
        venueId,
        hiddenCheckIds,
        snoozedRows,
      }),
    [
      opening,
      openingStatus,
      introMessage,
      venueId,
      hiddenCheckIds,
      snoozedRows,
    ],
  )
  const findingCards =
    isFindingThread || goingToFinding ? [] : openingDisplay.cards

  // Express shows each moment once; keep it for this open even if the
  // opening is re-read (Back from a thread), until they dismiss it.
  useEffect(() => {
    if (!visible) return
    const display = buildOllieMomentDisplay(opening, venueId)
    if (display) setMoment(display)
  }, [visible, opening, venueId])

  useEffect(() => {
    setFeedbackFollowUps({})
  }, [conversationId])

  useEffect(() => {
    if (!visible) return
    findingCards.forEach(card => {
      if (card.snoozed || shownCheckIdsRef.current.has(card.checkId)) return
      shownCheckIdsRef.current.add(card.checkId)
      markShown(card.finding, OLLIE_FINDING_SURFACES.PANEL)
    })
  }, [visible, findingCards, markShown])

  useEffect(() => {
    if (!visible) {
      hasInitializedRef.current = false
      return
    }

    if (!venueId || hasInitializedRef.current) {
      return
    }

    hasInitializedRef.current = true

    const init = async () => {
      try {
        await checkStatus()
        if (pendingFindingCheckId) return
        if (conversationId) {
          await loadMessages(conversationId, pageContext)
        } else {
          await openConversation(pageContext)
        }
      } catch (initError) {
        // Failures are stored in Redux by the Ollie thunks.
      }
    }

    init()
  }, [
    visible,
    venueId,
    conversationId,
    pageContext,
    pendingFindingCheckId,
    checkStatus,
    loadMessages,
    openConversation,
  ])

  useEffect(() => {
    if (!visible) {
      openedThreadRef.current = null
      return
    }
    if (isLoading || isLoadingMessages || !conversationId) return
    if (openedThreadRef.current === conversationId) return
    openedThreadRef.current = conversationId
    const target = ollieReopenScrollTargetId(messages)
    if (target) setAnswerToShowId(target)
  }, [visible, isLoading, isLoadingMessages, conversationId, messages])

  useEffect(() => {
    if (!visible) {
      return
    }
    const answer = answerToShowId
      ? messageScrollRef.current?.querySelector(
          `[data-ollie-message-id="${answerToShowId}"]`,
        )
      : null
    if (answer) {
      answer.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [
    visible,
    messages,
    isSending,
    animatingMessageId,
    answerToShowId,
    isLoading,
    isLoadingMessages,
  ])

  useEffect(() => {
    if (isSending) setAnswerToShowId(null)
  }, [isSending])

  const handleOverlayClick = useCallback(
    event => {
      if (event.target === event.currentTarget) onClose()
    },
    [onClose],
  )

  const handleCloseClick = useCallback(
    event => {
      event.stopPropagation()
      onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!visible) return undefined
    const onKeyDown = event => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [visible, onClose])

  const showAnsweredWhy = asked => {
    // A reopened thread opens at its latest reply; the replayed answer is
    // only a target until that has happened.
    if (!asked?.replayed) return
    if (openedThreadRef.current === asked.conversationId) return
    setAnswerToShowId(current => current || asked.assistantMessage?.id || null)
  }
  const showAnsweredWhyRef = useRef(showAnsweredWhy)
  showAnsweredWhyRef.current = showAnsweredWhy

  useEffect(() => {
    if (!wasSendingRef.current && isSending) {
      replyBeforeSendRef.current = latestOllieReplyId(messages)
      setRevealHold({ replyBefore: replyBeforeSendRef.current })
    }
    if (wasSendingRef.current && !isSending) {
      const latestId = latestOllieReplyId(messages)
      if (latestId && latestId !== replyBeforeSendRef.current) {
        setAnimatingMessageId(latestId)
      } else {
        setRevealHold(null)
      }
    }

    wasSendingRef.current = isSending
  }, [isSending, messages])

  useEffect(() => {
    setRevealHold(null)
  }, [conversationId])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const handleAnimateComplete = useCallback(() => {
    setAnimatingMessageId(null)
    setRevealHold(null)
    scrollToBottom()
  }, [scrollToBottom])

  const handleSend = async text => {
    if (
      canOrder &&
      beginOllieOrderingHandoff({
        venueId,
        text,
        history,
        closeOllie: onClose,
      })
    ) {
      return
    }

    try {
      await sendMessage(text, pageContext)
    } catch (sendError) {
      // Errors are stored in Redux; no extra handling needed here.
    }
  }

  const handleRetry = () => {
    Promise.resolve(retrySend()).catch(() => {})
  }

  const handlePromptSelect = prompt => {
    handleSend(prompt)
  }

  const goTo = path => {
    onClose()
    history.push(path)
  }

  const startAction = useCallback((checkId, action) => {
    setActionNotice(null)
    setActiveAction({
      key: `${checkId}:${action.id}:${Date.now()}`,
      checkId,
      action,
    })
  }, [])

  const openFindingThread = async card => {
    setActiveAction(null)
    setActionNotice(null)
    try {
      const outcome = await openFindingConversation(card.checkId, pageContext)
      return outcome?.success === false ? null : outcome
    } catch (threadError) {
      // Errors are stored in Redux.
      return null
    }
  }

  const openFindingThreadRef = useRef(openFindingThread)
  openFindingThreadRef.current = openFindingThread
  const pendingFindingActionRef = useRef(pendingFindingAction)
  pendingFindingActionRef.current = pendingFindingAction
  const pendingFindingAskRef = useRef(pendingFindingAsk)
  pendingFindingAskRef.current = pendingFindingAsk
  const askFindingWhyRef = useRef(askFindingWhy)
  askFindingWhyRef.current = askFindingWhy

  useEffect(() => {
    if (!visible || !venueId || !pendingFindingCheckId) return
    const checkId = pendingFindingCheckId
    const tap = resolveOllieActionTap(pendingFindingActionRef.current, venueId)
    const ask = pendingFindingAskRef.current
    if (typeof onPendingFindingConsumed === 'function') {
      onPendingFindingConsumed()
    }
    setOpeningFindingThread(true)
    const open = async () => {
      const outcome = await openFindingThreadRef.current({ checkId })
      if (outcome && tap.type === OLLIE_TAP_TYPES.ACTION) {
        startAction(checkId, tap.action)
      } else if (outcome && ask === 'why') {
        const asked = askFindingWhyRef.current(outcome, pageContext)
        showAnsweredWhyRef.current(asked)
      }
      if (!outcome) {
        try {
          await openConversation(pageContext)
        } catch (openError) {
          // Errors are stored in Redux.
        }
      }
      setOpeningFindingThread(false)
    }
    open()
  }, [
    visible,
    venueId,
    pendingFindingCheckId,
    onPendingFindingConsumed,
    openConversation,
    pageContext,
    startAction,
  ])

  const handleMomentAsk = async item => {
    setMoment(null)
    const outcome = await openFindingThread({ checkId: item.checkId })
    const threadId = outcome?.conversation?.id
    if (!threadId || !item.ask) return
    dispatch(
      sendOllieMessage({
        conversationId: threadId,
        message: item.ask,
        pageContext,
        venueId,
      }),
    )
  }

  const handleFindingAction = async card => {
    markClicked(card.finding, OLLIE_FINDING_SURFACES.PANEL)
    if (card.action?.mode === OLLIE_FINDING_ACTION_MODES.NAVIGATE) {
      goTo(card.action.path)
      return
    }
    const opened = await openFindingThread(card)
    const tap = resolveOllieActionTap(card.action, venueId)
    if (opened && tap.type === OLLIE_TAP_TYPES.ACTION) {
      startAction(card.checkId, tap.action)
    }
  }

  const handleChip = chip => {
    const tap = resolveOllieChipTap(chip, venueId)
    if (tap.type === OLLIE_TAP_TYPES.NONE) return
    const recorded = sendChip(chip, pageContext)
    if (tap.type === OLLIE_TAP_TYPES.ASK) return
    Promise.resolve(recorded).catch(() => {})
    if (tap.type === OLLIE_TAP_TYPES.NAVIGATE) {
      goTo(tap.path)
      return
    }
    if (finding?.checkId) startAction(finding.checkId, tap.action)
  }

  const handleReplyAction = async (message, action) => {
    setActionNotice(null)
    let checked = action
    try {
      const response = await tapReplyButton(message.id, action.index)
      checked = response?.action ? { ...action, ...response.action } : action
    } catch (tapError) {
      setActionNotice(
        tapError?.response?.data?.message ||
          "That isn't available any more. Ask Ollie again for the latest.",
      )
      return
    }
    const tap = resolveOllieActionTap(checked, venueId)
    if (tap.type === OLLIE_TAP_TYPES.NAVIGATE) goTo(tap.path)
    if (tap.type === OLLIE_TAP_TYPES.ACTION) {
      startAction(action.checkId || finding?.checkId, tap.action)
    }
  }

  const replyActionsFor = message =>
    (message.actions || []).filter(
      action =>
        action?.checkId &&
        resolveOllieActionTap(action, venueId).type !== OLLIE_TAP_TYPES.NONE,
    )

  const handleFindingWhy = async card => {
    markClicked(card.finding, OLLIE_FINDING_SURFACES.PANEL)
    const outcome = await openFindingThread(card)
    if (outcome) showAnsweredWhy(askFindingWhy(outcome, pageContext))
  }

  const setSnoozedRow = (checkId, row) =>
    setSnoozedRows(previous => {
      const next = { ...previous }
      if (row) next[checkId] = row
      else delete next[checkId]
      return next
    })

  const handleFindingSnooze = card => {
    const { checkId } = card
    setHiddenCheckIds(previous => [...previous, checkId])
    setSnoozedRow(checkId, {
      hiddenUntilLabel: ollieHiddenUntilLabel(
        fallbackOllieHiddenUntil(Date.now(), card.finding?.snoozeDays),
      ),
    })
    const request = Promise.resolve(
      snooze(card.finding, OLLIE_FINDING_SURFACES.PANEL),
    ).catch(() => null)
    snoozeRequestsRef.current[checkId] = request
    request
      .then(outcome => {
        if (snoozeRequestsRef.current[checkId] !== request) return null
        if (outcome?.success === false) return null
        setSnoozedRows(previous =>
          previous[checkId]
            ? {
                ...previous,
                [checkId]: {
                  ...previous[checkId],
                  hiddenUntilLabel: ollieHiddenUntilLabel(
                    resolveOllieHiddenUntil(
                      outcome,
                      Date.now(),
                      card.finding?.snoozeDays,
                    ),
                  ),
                },
              }
            : previous,
        )
        return loadStarterPrompts(pageContext)
      })
      .catch(() => {})
  }

  const handleUndoSnooze = async card => {
    const { checkId } = card
    const row = snoozedRows[checkId]
    setHiddenCheckIds(previous => previous.filter(id => id !== checkId))
    setSnoozedRow(checkId, null)
    // Undo after the snooze lands, or Express would clear nothing and then hide it.
    await snoozeRequestsRef.current[checkId]
    delete snoozeRequestsRef.current[checkId]
    const outcome = await unsnooze(card.finding, OLLIE_FINDING_SURFACES.PANEL)
    if (outcome && outcome.success !== false) {
      loadStarterPrompts(pageContext)
      return
    }
    setHiddenCheckIds(previous =>
      previous.includes(checkId) ? previous : [...previous, checkId],
    )
    setSnoozedRow(checkId, { ...row, undoFailed: true })
  }

  const handleTodayJob = path => goTo(path)

  const handleBackToOpening = async () => {
    openingLoadedRef.current = false
    setActiveAction(null)
    setActionNotice(null)
    setFeedbackFollowUps({})
    clearThread()
    try {
      await openConversation(pageContext)
    } catch (openError) {
      // Errors are stored in Redux.
    }
  }

  const handleFeedback = async (messageId, rating, comment = '') => {
    try {
      const outcome = await submitFeedback(messageId, rating, comment)
      if (rating === 'not_helpful' && outcome?.followUp) {
        setFeedbackFollowUps(previous => ({
          ...previous,
          [messageId]: outcome.followUp,
        }))
      }
    } catch (feedbackError) {
      // Errors are stored in Redux.
    }
  }

  const inputDisabled =
    !isAvailable || limitReached || isLoading || isLoadingMessages
  const chipAskDisabled = isOllieChipAskDisabled({
    unavailable: !isAvailable || isLoading || isLoadingMessages,
    limitReached,
    finding,
    followUp,
  })
  const visibleStarterPrompts = filterOllieStarterPrompts(starterPrompts, {
    opening,
    hiddenCheckIds,
  })
  const showStarterPrompts = shouldShowOllieStarterPrompts({
    isFindingThread,
    messages,
    prompts: visibleStarterPrompts,
    isSending,
    limitReached,
  })
  const replyRevealing = isOllieReplyRevealing({
    isSending,
    animatingMessageId,
    revealHold,
    messages,
  })
  const latestReplyId = latestOllieReplyId(messages)
  const latestReply = messages.find(item => item.id === latestReplyId)
  const threadChips = isFindingThread
    ? withoutChipsMatchingReply(
        listUsableOllieChips(chips, venueId, messages),
        latestReply ? replyActionsFor(latestReply) : [],
        venueId,
      )
    : []
  // The reply lands one render before animatingMessageId is set; type it from that first frame.
  const revealingReplyId =
    animatingMessageId ||
    (revealHold && !isSending && latestReplyId !== revealHold.replyBefore
      ? latestReplyId
      : null)
  const freeFollowUpLine = isFindingThread
    ? ollieFreeFollowUpLine(followUp)
    : null

  if (!visible) {
    return null
  }

  return (
    <Overlay
      data-testid='ollie-chat-overlay'
      onClick={handleOverlayClick}
      aria-hidden='false'
    >
      <Dialog
        role='dialog'
        aria-modal='true'
        aria-label='Ollie chat'
        onClick={event => event.stopPropagation()}
      >
        <Header>
          <HeaderBrand>
            <OllieIcon size={32} variant='onBrand' />
            <HeaderText>
              <Title>Ollie</Title>
              <Subtitle>{OLLIE_SUBTITLE}</Subtitle>
            </HeaderText>
          </HeaderBrand>
          <CloseButton
            type='button'
            data-testid='ollie-chat-close'
            onClick={handleCloseClick}
            aria-label='Close Ollie'
          >
            <CloseOutlined />
          </CloseButton>
        </Header>

        <Body>
          {!venueId && (
            <PanelAlert
              type='warning'
              message='Select a venue to chat with Ollie'
              showIcon
            />
          )}

          {venueId && !enabled && (
            <PanelAlert
              type='info'
              message='Ollie is not available right now'
              showIcon
            />
          )}

          {venueId && enabled && !ready && (
            <PanelAlert
              type='info'
              message='Ollie is being set up — please try again shortly'
              showIcon
            />
          )}

          {error && (
            <PanelAlert type='error' message={error} showIcon closable />
          )}

          {limitReached && (
            <LimitHint>
              Message limit reached.{' '}
              {onShowPaywall && (
                <LimitLink type='button' onClick={onShowPaywall}>
                  View upgrade options
                </LimitLink>
              )}
            </LimitHint>
          )}

          {usageWarning?.message && (
            <PanelAlert type='info' message={usageWarning.message} showIcon />
          )}

          {(isLoading || isLoadingMessages) && (
            <LoadingWrap>
              <Spin size='large' />
              <LoadingText>Loading Ollie…</LoadingText>
            </LoadingWrap>
          )}

          {!isLoading && !isLoadingMessages && (
            <MessageScroll ref={messageScrollRef}>
              {isFindingThread && (
                <BackLink type='button' onClick={handleBackToOpening}>
                  ← {OLLIE_FINDING_CARD_COPY.back}
                </BackLink>
              )}

              {isFindingThread ? (
                introMessage && (
                  <IntroBubble>
                    <IntroLabel>Ollie</IntroLabel>
                    <IntroText>{introMessage}</IntroText>
                    {finding?.stillTrueLine && (
                      <StaleNote data-testid='ollie-still-true'>
                        {finding.stillTrueLine}
                      </StaleNote>
                    )}
                  </IntroBubble>
                )
              ) : moment ? (
                <OllieMomentCard
                  moment={moment}
                  onAsk={handleMomentAsk}
                  onTodayJob={handleTodayJob}
                  onDismiss={() => setMoment(null)}
                  disabled={isSending}
                />
              ) : (
                openingDisplay.message && (
                  <IntroBubble aria-busy={openingDisplay.mode === 'loading'}>
                    <IntroLabel>Ollie</IntroLabel>
                    <IntroText $muted={openingDisplay.mode === 'loading'}>
                      {openingDisplay.message}
                    </IntroText>
                    {openingDisplay.lines.map(line => (
                      <IntroLine key={line}>{line}</IntroLine>
                    ))}
                    {openingDisplay.winLine && (
                      <IntroLine>{openingDisplay.winLine}</IntroLine>
                    )}
                    {openingDisplay.todayJob && (
                      <TodayJobRow>
                        {openingDisplay.todayJob.path ? (
                          <TodayJobButton
                            type='button'
                            onClick={() =>
                              handleTodayJob(openingDisplay.todayJob.path)
                            }
                          >
                            {openingDisplay.todayJob.label}
                          </TodayJobButton>
                        ) : (
                          <TodayJobNote data-testid='ollie-today-job-note'>
                            {openingDisplay.todayJob.note}
                          </TodayJobNote>
                        )}
                        {openingDisplay.todayJob.minutesLabel && (
                          <TodayJobMeta>
                            {openingDisplay.todayJob.minutesLabel}
                          </TodayJobMeta>
                        )}
                      </TodayJobRow>
                    )}
                    {openingDisplay.staleNote && (
                      <StaleNote>{openingDisplay.staleNote}</StaleNote>
                    )}
                  </IntroBubble>
                )
              )}

              <OllieFindingCards
                cards={findingCards}
                onAction={handleFindingAction}
                onWhy={handleFindingWhy}
                onSnooze={handleFindingSnooze}
                onUndoSnooze={handleUndoSnooze}
                disabled={isSending}
              />

              {messages.map(item => (
                <OllieMessageBubble
                  key={item.id}
                  message={item}
                  onFeedback={handleFeedback}
                  isSubmittingFeedback={isSubmittingFeedback}
                  animate={item.id === revealingReplyId}
                  onAnimateComplete={handleAnimateComplete}
                  onAnimateTick={scrollToBottom}
                  actions={
                    item.id === latestReplyId ? replyActionsFor(item) : []
                  }
                  onAction={handleReplyAction}
                  feedbackFollowUp={feedbackFollowUps[item.id] || null}
                  actionsDisabled={isSending}
                />
              ))}

              {!isSending &&
                lastFailedSend &&
                lastFailedSend.conversationId === conversationId && (
                  <OllieRetryRow
                    reason={lastFailedSend.reason}
                    onRetry={handleRetry}
                    disabled={inputDisabled}
                  />
                )}

              {isSending && <OllieTypingIndicator />}

              {actionNotice && (
                <PanelAlert
                  type='info'
                  message={actionNotice}
                  showIcon
                  closable
                  onClose={() => setActionNotice(null)}
                />
              )}

              {activeAction && (
                <OllieActionSheet
                  key={activeAction.key}
                  checkId={activeAction.checkId}
                  action={activeAction.action}
                  venueId={venueId}
                  onClose={() => setActiveAction(null)}
                  onNavigate={goTo}
                  onShowPaywall={onShowPaywall}
                />
              )}

              {isFindingThread && !replyRevealing && (
                <>
                  <OllieFindingChips
                    chips={threadChips}
                    onSelect={handleChip}
                    disabled={isSending}
                    askDisabled={chipAskDisabled}
                  />
                  {freeFollowUpLine && (
                    <FollowUpLine>{freeFollowUpLine}</FollowUpLine>
                  )}
                  <OllieReportFinding
                    key={finding.checkId}
                    checkId={finding.checkId}
                  />
                </>
              )}

              {showStarterPrompts && (
                <OllieStarterPrompts
                  prompts={visibleStarterPrompts}
                  onSelect={handlePromptSelect}
                  disabled={inputDisabled || isSending}
                />
              )}

              <div ref={messagesEndRef} />
            </MessageScroll>
          )}
        </Body>

        <Footer>
          {((usage && !limitReached) || opening?.feedback) && (
            <FooterMeta>
              {usage && !limitReached ? (
                <UsageBar>
                  {usage.monthlyRemaining != null
                    ? `${usage.monthlyRemaining} messages left this month`
                    : 'Unlimited messages this month'}
                </UsageBar>
              ) : (
                <span />
              )}
              {opening?.feedback && (
                <FeedbackLink href={opening.feedback.mailto}>
                  {opening.feedback.label}
                </FeedbackLink>
              )}
            </FooterMeta>
          )}

          <OllieChatInput
            onSend={handleSend}
            disabled={inputDisabled}
            isSending={isSending}
            draft={draftMessage}
            onDraftConsumed={onDraftConsumed}
            placeholder={
              limitReached
                ? 'Message limit reached'
                : 'Ask Ollie anything about OMS…'
            }
          />
        </Footer>
      </Dialog>
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1050;
  pointer-events: none;
  animation: ollieOverlayIn 150ms ease-out;

  @keyframes ollieOverlayIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Dialog = styled.div`
  width: 100%;
  max-width: 720px;
  height: min(720px, calc(100vh - 40px));
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colours.bgGrey};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  pointer-events: auto;
  animation: ollieDialogIn 150ms ease-out;

  @keyframes ollieDialogIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 480px) {
    max-width: 100%;
    height: calc(100vh - 24px);
    max-height: calc(100vh - 24px);
    border-radius: 14px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  flex-shrink: 0;
`

const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Subtitle = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  opacity: 0.85;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    opacity: 0.85;
  }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 20px 0;
`

const PanelAlert = styled(Alert)`
  margin-bottom: 10px;
`

const LimitHint = styled.p`
  margin: 0 0 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  font-size: 13px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const LimitLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack};
  text-decoration: underline;
  cursor: pointer;
`

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
`

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 14px;
`

const MessageScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 12px;
`

const IntroBubble = styled.div`
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 14px 14px 14px 4px;
  padding: 14px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`

const IntroLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colours.oliveGrey};
  margin-bottom: 4px;
`

const IntroText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme, $muted }) =>
    $muted ? theme.colours.oliveGrey : theme.colours.bbqBlack};
`

const IntroLine = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const TodayJobRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`

const TodayJobButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`

const TodayJobMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const TodayJobNote = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const StaleNote = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const FollowUpLine = styled.p`
  margin: -4px 0 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const BackLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack};
  text-decoration: underline;
  cursor: pointer;
`

const Footer = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver};
  padding-bottom: max(32px, env(safe-area-inset-bottom, 0px));
`

const FooterMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 8px 16px 0;
`

const UsageBar = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const FeedbackLink = styled.a`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colours.bbqBlack};
  }
`

export default OllieChatPanel
